import React, { useState, useEffect, useContext, useRef, useCallback } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	Switch,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import moment from "moment"
import { AuthContext } from "../../context/Auth/AuthContext";
import { isArray, capitalize, debounce } from "lodash";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import AttachFile from "@material-ui/icons/AttachFile";
import { head } from "lodash";
import ConfirmationModal from "../ConfirmationModal";
import MessageVariablesPicker from "../MessageVariablesPicker";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
}));

// As mensagens são funções porque o schema é criado no import do módulo, antes de
// o i18n resolver o idioma — assim a tradução só é lida na hora de validar.
const ScheduleSchema = Yup.object().shape({
	body: Yup.string()
		.min(5, () => i18n.t("scheduleModal.validation.bodyMin"))
		.required(() => i18n.t("scheduleModal.validation.required")),
	contactId: Yup.number().required(() => i18n.t("scheduleModal.validation.required")),
	sendAt: Yup.string()
		.required(() => i18n.t("scheduleModal.validation.required"))
		.test(
			"not-in-past",
			() => i18n.t("scheduleModal.validation.sendAtPast"),
			(value) => {
				if (!value) return false;
				const selectedDate = moment(value);
				if (!selectedDate.isValid()) return false;
				return selectedDate.isSameOrAfter(moment().subtract(2, "minutes"));
			}
		),
	whatsappId: Yup.number().required(() =>
		i18n.t("scheduleModal.validation.whatsappRequired")
	)
});

const ScheduleModal = ({ open, onClose, scheduleId, contactId, cleanContact, reload }) => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);

	const initialState = {
		body: "",
		contactId: "",
		sendAt: moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
		sentAt: "",
		whatsappId: "",
		// Mesmo padrão do chat, onde o botão "Assinar" já vem ligado.
		signMessage: true
	};

	const initialContact = {
		id: "",
		name: "",
		number: ""
	}

	const [schedule, setSchedule] = useState(initialState);
	const [currentContact, setCurrentContact] = useState(initialContact);
	const [contacts, setContacts] = useState([initialContact]);
	const [loading, setLoading] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [attachment, setAttachment] = useState(null);
	const attachmentFile = useRef(null);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const messageInputRef = useRef();
	const [whatsapps, setWhatsapps] = useState([]);
	// Ids de todas as conexões vivas (o /whatsapp não devolve as excluídas), antes
	// do filtro de CONNECTED. `null` = ainda não carregou.
	const [liveWhatsappIds, setLiveWhatsappIds] = useState(null);
	const minSendAt = moment().subtract(2, "minutes").format("YYYY-MM-DDTHH:mm");

	const ensureContactInOptions = useCallback((contact) => {
		if (!contact || !contact.id) return;

		setContacts((prevContacts) => {
			const exists = prevContacts.some((item) => Number(item.id) === Number(contact.id));
			if (exists) return prevContacts;

			return [
				{ id: "", name: "", number: "" },
				...prevContacts.filter((item) => item.id),
				{ id: contact.id, name: contact.name, number: contact.number }
			];
		});
	}, []);

	// Função para buscar contatos com debounce
	const searchContacts = useCallback(
		debounce(async (searchValue, companyId) => {
			if (!searchValue || searchValue.length < 2) {
				setLoading(false);
				return;
			}
			try {
				setLoading(true);
				const { data: contactList } = await api.get('/contacts/list', { 
					params: { 
						companyId: companyId,
						searchParam: searchValue,
						limit: 20
					} 
				});
				let customList = contactList.map((c) => ({ id: c.id, name: c.name, number: c.number }));
				if (isArray(customList)) {
					setContacts((prevContacts) => {
						const previousContacts = prevContacts.filter((item) => item.id);
						const mergedContacts = [...previousContacts, ...customList].filter(
							(item, index, arr) => arr.findIndex((entry) => Number(entry.id) === Number(item.id)) === index
						);
						return [{ id: "", name: "", number: "" }, ...mergedContacts];
					});
				}
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		}, 500),
		[]
	);

	useEffect(() => {
		if (contactId && contacts.length) {
			const contact = contacts.find(c => Number(c.id) === Number(contactId));
			if (contact) {
				setCurrentContact(contact);
				ensureContactInOptions(contact);
			}
		}
	}, [contactId, contacts, ensureContactInOptions]);

	// Buscar conexões disponíveis
	useEffect(() => {
		const fetchWhatsapps = async () => {
			try {
				const { data } = await api.get("/whatsapp/?session=0");
				setLiveWhatsappIds((data || []).map(w => Number(w.id)));
				// Filtrar apenas conexões conectadas
				const connectedWhatsapps = data.filter(w => w.status === "CONNECTED");
				setWhatsapps(connectedWhatsapps);
			} catch (err) {
				toastError(err);
			}
		};
		if (open) {
			fetchWhatsapps();
		}
	}, [open]);

	useEffect(() => {
		const { companyId } = user;
		if (open) {
			// Limpar estados ao abrir o modal
			setSearchText("");
			setContacts([initialContact]);
			
			try {
				(async () => {
					// Se tem contactId definido, buscar apenas esse contato
					if (contactId) {
						const { data: contactList } = await api.get('/contacts/list', { 
							params: { companyId: companyId, searchParam: '', limit: 1, contactId: contactId } 
						});
						if (contactList && contactList.length > 0) {
							const contact = { id: contactList[0].id, name: contactList[0].name, number: contactList[0].number };
							ensureContactInOptions(contact);
							setCurrentContact(contact);
							setSearchText(contact.number ? `${contact.name} - ${contact.number}` : contact.name);
						}
						setSchedule(prevState => {
							return { ...prevState, contactId }
						});
					}

					if (!scheduleId) return;

					const { data } = await api.get(`/schedules/${scheduleId}`);
					setSchedule(prevState => {
						return {
								...prevState,
								...data,
								// Agendamento criado antes da coluna existir vem sem o campo.
								signMessage: data.signMessage ?? true,
								sendAt: moment(data.sendAt).format('YYYY-MM-DDTHH:mm')
							};
					});
					if (data.contact) {
						setCurrentContact(data.contact);
						ensureContactInOptions(data.contact);
						setSearchText(data.contact.number ? `${data.contact.name} - ${data.contact.number}` : data.contact.name);
					}
				})()
			} catch (err) {
				toastError(err);
			}
		}
	}, [scheduleId, contactId, open, user, ensureContactInOptions]);

	// Agendamento de uma conexão excluída: a conexão não pode seguir escondida no
	// formulário — limpa para o campo obrigatório pedir outra. Conexão só
	// desconectada continua como estava.
	useEffect(() => {
		if (!open || !liveWhatsappIds || !schedule?.whatsappId) return;
		if (!liveWhatsappIds.includes(Number(schedule.whatsappId))) {
			setSchedule(prev => ({ ...prev, whatsappId: "" }));
		}
	}, [open, liveWhatsappIds, schedule?.whatsappId]);

	const handleClose = () => {
		onClose();
		setAttachment(null);
		setSchedule(initialState);
		setCurrentContact(initialContact);
		setContacts([initialContact]);
		setSearchText("");
	};

	const handleAttachmentFile = (e) => {
		const file = head(e.target.files);
		if (file) {
			setAttachment(file);
		}
	};

	const handleSaveSchedule = async values => {
		const scheduleData = { ...values, userId: user.id };
		try {
			if (scheduleId) {
				await api.put(`/schedules/${scheduleId}`, scheduleData);
				if (attachment != null) {
					const formData = new FormData();
					formData.append("file", attachment);
					await api.post(
						`/schedules/${scheduleId}/media-upload`,
						formData
					);
				}
			} else {
				const { data } = await api.post("/schedules", scheduleData);
				if (attachment != null) {
					const formData = new FormData();
					formData.append("file", attachment);
					await api.post(`/schedules/${data.id}/media-upload`, formData);
				}
			}
			toast.success(i18n.t("scheduleModal.success"));
			if (typeof reload == 'function') {
				reload();
			}
			if (contactId) {
				if (typeof cleanContact === 'function') {
					cleanContact();
					history.push('/schedules');
				}
			}
		} catch (err) {
			toastError(err);
		}
		setCurrentContact(initialContact);
		setSchedule(initialState);
		handleClose();
	};
	const handleClickMsgVar = async (msgVar, setValueFunc) => {
		const el = messageInputRef.current;
		const firstHalfText = el.value.substring(0, el.selectionStart);
		const secondHalfText = el.value.substring(el.selectionEnd);
		const newCursorPos = el.selectionStart + msgVar.length;

		setValueFunc("body", `${firstHalfText}${msgVar}${secondHalfText}`);

		await new Promise(r => setTimeout(r, 100));
		messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
	};

	const deleteMedia = async () => {
		if (attachment) {
			setAttachment(null);
			attachmentFile.current.value = null;
		}

		if (schedule.mediaPath) {
			await api.delete(`/schedules/${schedule.id}/media-upload`);
			setSchedule((prev) => ({
				...prev,
				mediaPath: null,
			}));
			toast.success(i18n.t("scheduleModal.toasts.deleted"));
			if (typeof reload == "function") {
				console.log(reload);
				console.log("1");
				reload();
			}
		}
	};

	return (
		<div className={classes.root}>
			<ConfirmationModal
				title={i18n.t("scheduleModal.confirmationModal.deleteTitle")}
				open={confirmationOpen}
				onClose={() => setConfirmationOpen(false)}
				onConfirm={deleteMedia}
			>
				{i18n.t("scheduleModal.confirmationModal.deleteMessage")}
			</ConfirmationModal>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{schedule.status
						? i18n.t(`scheduleModal.title.${schedule.status}`, {
							defaultValue: `${i18n.t("scheduleModal.form.body")} ${capitalize(schedule.status)}`
						})
						: i18n.t("scheduleModal.title.add")}
				</DialogTitle>
				<div style={{ display: "none" }}>
					<input
						type="file"
						accept=".png,.jpg,.jpeg"
						ref={attachmentFile}
						onChange={(e) => handleAttachmentFile(e)}
					/>
				</div>
				<Formik
					initialValues={schedule}
					enableReinitialize={true}
					validationSchema={ScheduleSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveSchedule(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<FormControl
										variant="outlined"
										fullWidth
									>
										<Autocomplete
											fullWidth
											value={currentContact}
											options={contacts}
											loading={loading}
											inputValue={searchText}
											onInputChange={(event, newInputValue, reason) => {
												if (reason === 'input') {
													setSearchText(newInputValue);
													searchContacts(newInputValue, user.companyId);
												} else if (reason === 'reset') {
													setSearchText(newInputValue);
												} else if (reason === 'clear') {
													setSearchText("");
													setFieldValue("contactId", "");
													setCurrentContact(initialContact);
												}
											}}
											onChange={(e, contact) => {
												const contactId = contact ? contact.id : '';
												setFieldValue("contactId", contactId);
												setCurrentContact(contact ? contact : initialContact);
												if (contact) {
													ensureContactInOptions(contact);
													setSearchText(contact.number ? `${contact.name} - ${contact.number}` : contact.name);
												}
											}}
											noOptionsText={searchText.length < 2 ? i18n.t("scheduleModal.form.contactMinChars") : i18n.t("scheduleModal.form.contactNotFound")}
											loadingText={i18n.t("scheduleModal.form.contactLoading")}
										getOptionLabel={(option) => {
											if (!option || !option.name) return "";
											return option.number ? `${option.name} - ${option.number}` : option.name;
										}}
										getOptionSelected={(option, value) => {
											return value.id === option.id
										}}
										renderOption={(option) => (
											<div>
												<div style={{ fontWeight: 500 }}>{option.name}</div>
												{option.number && (
													<div style={{ fontSize: '0.85em', color: '#666' }}>{option.number}</div>
												)}
											</div>
										)}
										renderInput={(params) => (
											<TextField 
												{...params} 
												variant="outlined" 
												placeholder={i18n.t("scheduleModal.form.contactSearchPlaceholder")}
												InputProps={{
													...params.InputProps,
													endAdornment: (
														<>
															{loading ? <CircularProgress color="inherit" size={20} /> : null}
															{params.InputProps.endAdornment}
														</>
													),
												}}
											/>
										)}
										/>
									</FormControl>
								</div>
								<br />
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										rows={9}
										multiline={true}
										label={i18n.t("scheduleModal.form.body")}
										name="body"
										inputRef={messageInputRef}
										error={touched.body && Boolean(errors.body)}
										helperText={touched.body && errors.body}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
								</div>
								<Grid item>
									<MessageVariablesPicker
										disabled={isSubmitting}
										onClick={value => handleClickMsgVar(value, setFieldValue)}
									/>
								</Grid>
								<br />
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("scheduleModal.form.sendAt")}
										type="datetime-local"
										name="sendAt"
										inputProps={{ min: minSendAt }}
										InputLabelProps={{
											shrink: true,
										}}
										error={touched.sendAt && Boolean(errors.sendAt)}
										helperText={touched.sendAt && errors.sendAt}
										variant="outlined"
										fullWidth
									/>
								</div>
								<br />
								<div className={classes.multFieldLine}>
									<FormControl 
										variant="outlined" 
										fullWidth 
										error={touched.whatsappId && Boolean(errors.whatsappId)}
									>
										<InputLabel id="whatsapp-select-label">
											{i18n.t("scheduleModal.form.whatsapp")}
										</InputLabel>
										<Field
											as={Select}
											labelId="whatsapp-select-label"
											label={i18n.t("scheduleModal.form.whatsapp")}
											name="whatsappId"
											value={values.whatsappId || ""}
											onChange={(e) => setFieldValue("whatsappId", e.target.value)}
										>
											<MenuItem value="">
												<em>{i18n.t("scheduleModal.form.whatsappPlaceholder")}</em>
											</MenuItem>
											{whatsapps.map((whatsapp) => (
												<MenuItem key={whatsapp.id} value={whatsapp.id}>
													{whatsapp.name}
												</MenuItem>
											))}
										</Field>
										{touched.whatsappId && errors.whatsappId && (
											<span style={{ color: "#f44336", fontSize: "0.75rem", marginLeft: 14, marginTop: 3 }}>
												{errors.whatsappId}
											</span>
										)}
									</FormControl>
								</div>
								<div className={classes.multFieldLine}>
									<FormControlLabel
										control={
											<Switch
												checked={Boolean(values.signMessage)}
												onChange={(e) =>
													setFieldValue("signMessage", e.target.checked)
												}
												name="signMessage"
												color="primary"
											/>
										}
										label={i18n.t("scheduleModal.form.signMessage")}
									/>
								</div>
								{(schedule.mediaPath || attachment) && (
									<Grid xs={12} item>
										<Button startIcon={<AttachFile />}>
											{attachment ? attachment.name : schedule.mediaName}
										</Button>
										<IconButton
											onClick={() => setConfirmationOpen(true)}
											color="secondary"
										>
											<DeleteOutline color="secondary" />
										</IconButton>
									</Grid>
								)}
							</DialogContent>
							<DialogActions>
								{!attachment && !schedule.mediaPath && (
									<Button
										color="primary"
										onClick={() => attachmentFile.current.click()}
										disabled={isSubmitting}
										variant="outlined"
									>
										{i18n.t("quickMessages.buttons.attach")}
									</Button>
								)}
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("scheduleModal.buttons.cancel")}
								</Button>
								{(schedule.sentAt === null || schedule.sentAt === "") && (
									<Button
										type="submit"
										color="primary"
										disabled={isSubmitting}
										variant="contained"
										className={classes.btnWrapper}
									>
										{scheduleId
											? `${i18n.t("scheduleModal.buttons.okEdit")}`
											: `${i18n.t("scheduleModal.buttons.okAdd")}`}
										{isSubmitting && (
											<CircularProgress
												size={24}
												className={classes.buttonProgress}
											/>
										)}
									</Button>
								)}
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ScheduleModal;